import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { OtpInput } from "@/components/forms/otpInput"
import { AuthWrapper } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { useResendVerification, useVerifyEmail } from "../hooks/useAuth"
import { verifyEmailByLink } from "../services/auth.service"
import { useAuthStore } from "../store/authStore"

/**
 * EmailVerificationScreen - Handles both OTP and magic link verification
 */
export function EmailVerificationScreen() {
  const { t } = useTranslation("auth")
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const verifyEmailMutation = useVerifyEmail()
  const resendVerificationMutation = useResendVerification()

  // Check for magic link params
  const magicEmail = searchParams.get("email")
  const magicToken = searchParams.get("token")

  // Get email from location state (passed from signup) or from query param
  const stateEmail = (location.state as { email?: string })?.email
  const email = magicEmail || stateEmail

  const [otp, setOtp] = useState("")
  const [resendCountdown, setResendCountdown] = useState(60)
  const [isVerifyingLink, setIsVerifyingLink] = useState(false)
  const verifyInProgress = useRef(false)

  const isOtpComplete = otp.length === 6
  const isLoading = verifyEmailMutation.isPending || isVerifyingLink

  // Handle magic link verification on page load
  const verifyMagicLink = useCallback(async () => {
    if (!magicEmail || !magicToken || verifyInProgress.current) {
      return
    }

    verifyInProgress.current = true
    setIsVerifyingLink(true)

    try {
      const result = await verifyEmailByLink(magicEmail, magicToken)
      useAuthStore.getState().setUser(result.user)
      window.location.href = "/onboarding/permissions"
    } catch {
      toast.error("Invalid or expired verification code")
      window.location.href = "/verify-email"
    } finally {
      setIsVerifyingLink(false)
      verifyInProgress.current = false
    }
  }, [magicEmail, magicToken])

  useEffect(() => {
    verifyMagicLink()
  }, [verifyMagicLink])

  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCountdown])

  const handleVerify = async () => {
    if (!isOtpComplete || !email) return

    // Get device info
    const deviceId = localStorage.getItem("deviceId") || crypto.randomUUID()
    localStorage.setItem("deviceId", deviceId)

    const deviceType = /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop"
    const userAgent = navigator.userAgent

    try {
      await verifyEmailMutation.mutateAsync({
        email,
        code: otp,
        deviceId,
        deviceType,
        userAgent,
      })

      // Navigate to permissions on success
      navigate("/onboarding/permissions", { replace: true })
    } catch (error) {
      // Error is handled by the hook and displayed via toast
      console.error("Verification error:", error)
    }
  }

  const handleResendCode = async () => {
    if (resendCountdown > 0) return
    if (!email) return

    try {
      await resendVerificationMutation.mutateAsync({ email })
      // Reset countdown
      setResendCountdown(60)
      // Clear OTP
      setOtp("")
    } catch (error) {
      console.error("Resend error:", error)
    }
  }

  return (
    <AuthWrapper className="my-29">
      {/* Title */}
      <div className="text-center mt-5">
        <h2 className="text-[24px] font-semibold text-primary font-monteserat mt-1">
          {t("verifyEmail.title")}
        </h2>
      </div>

      {/* Subtitle */}
      <div className="text-center mt-2 space-y-1">
        <p className="text-[14px] leading-[25px] text-primary font-monteserat px-5">
          We've sent a confirmation link to{" "}
          <span className="font-semibold">{email || "your email"}</span>. <br />
          Please click the link in the email or enter the verification code below.
        </p>
      </div>

      {/* OTP Input */}
      <div className="flex justify-center mt-10">
        <OtpInput
          length={6}
          value={otp}
          onChange={setOtp}
          onComplete={handleVerify}
          disabled={isLoading}
          autoFocus
        />
      </div>

      {/* Resend Link */}
      <div className="text-center mt-10">
        <p className="text-[14px] font-monteserat text-primary">
          Didn't receive the email? Check your spam folder or{" "}
          {resendCountdown > 0 ? (
            <span className="font-semibold p-0 h-auto text-primary">
              {t("verifyEmail.resendCountdown", { resendCountdown })}
            </span>
          ) : (
            <Button
              type="button"
              variant="link"
              onClick={handleResendCode}
              className="font-semibold p-0 h-auto text-primary"
              disabled={isLoading}
            >
              Resend
            </Button>
          )}
        </p>
      </div>

      {/* Continue Button */}
      <div className="flex justify-center mt-16">
        <Button
          type="button"
          variant="primary"
          size="md"
          className="max-w-[220px] uppercase"
          isLoading={isLoading}
          disabled={!isOtpComplete}
          fullWidth
          onClick={handleVerify}
        >
          Continue
        </Button>
      </div>
    </AuthWrapper>
  )
}
